import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { NgClass } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { DialogComponent } from '../dialog/dialog.component';

export interface AddressValidationDialogAddress {
  zip: string;
  city: string;
  street?: string;
  number?: string;
}

export interface AddressValidationSuggestionsDialogData {
  enteredAddress: AddressValidationDialogAddress;
  suggestions: AddressValidationDialogAddress[];
}

export type AddressValidationSuggestionsDialogResult =
  | { action: 'edit' }
  | { action: 'continue' }
  | { action: 'applySuggestion'; address: AddressValidationDialogAddress };

@Component({
  selector: 'ui-address-validation-suggestions-dialog',
  imports: [NgClass, MatButtonModule, DialogComponent],
  templateUrl: './address-validation-suggestions-dialog.component.html',
  styleUrl: './address-validation-suggestions-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddressValidationSuggestionsDialogComponent {
  readonly data = inject<AddressValidationSuggestionsDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(
    MatDialogRef<
      AddressValidationSuggestionsDialogComponent,
      AddressValidationSuggestionsDialogResult | undefined
    >
  );

  readonly suggestions = this.data.suggestions.slice(0, 5);
  readonly hasSuggestions = this.suggestions.length > 0;
  readonly selectedIndex = signal(0);

  readonly formattedEnteredAddress = this.formatAddress(this.data.enteredAddress);
  readonly formattedSuggestions = this.suggestions.map(s => this.formatAddress(s));

  selectSuggestion(index: number) {
    this.selectedIndex.set(index);
  }

  closeAndEdit() {
    this.dialogRef.close({ action: 'edit' });
  }

  continueWithEnteredAddress() {
    this.dialogRef.close({ action: 'continue' });
  }

  applySuggestion() {
    if (!this.hasSuggestions) return;
    const selected = this.suggestions[this.selectedIndex()];
    if (!selected) return;
    this.dialogRef.close({ action: 'applySuggestion', address: selected });
  }

  private formatAddress(address: AddressValidationDialogAddress): string {
    const streetPart = [address.street, address.number].filter(Boolean).join(' ').trim();
    const cityPart = [address.zip, address.city].filter(Boolean).join(' ').trim();
    return [streetPart, cityPart].filter(Boolean).join(', ');
  }
}
